import type { Clip, MidiClip, Project, Track } from './types';
import { clipEndTicks } from './selectors';
import { clipsOnLane, maxTakeLane, sliceClip } from './slice';

/**
 * What a take is called.
 *
 * A take is a performance, not a lane number. The lane a pass sits on is an
 * accident of what else has been recorded and deleted since — delete take 2 and
 * take 3 slides down into its place — so a name derived only from the lane index
 * renames a performance behind the user's back. Silently, too: the third pass,
 * the one that was good, is suddenly called "Take 2", and the note the user made
 * about take 3 now points at someone else's playing.
 *
 * So names are stored, keyed by lane, and they travel with their material when
 * lanes renumber. A lane without a stored name falls back to its number, which
 * is what a project made before this existed looks like, and what an unnamed
 * lane should read as anyway.
 */

/** The most a take name may be, in characters. */
export const MAX_TAKE_NAME = 64;

/** What an unnamed lane is called. */
export function defaultTakeName(lane: number): string {
  return `Take ${lane}`;
}

/** What to show for a take lane: its stored name, or its lane number. */
export function takeName(project: Project, trackId: string, lane: number): string {
  return trackTakeName(project.tracks[trackId], lane);
}

/** As `takeName`, when the track is already in hand. */
export function trackTakeName(track: Track | undefined, lane: number): string {
  const stored = track?.takeNames[String(lane)];
  return stored && stored.trim() ? stored : defaultTakeName(lane);
}

/** True when the lane carries a name of its own rather than falling back. */
export function hasTakeName(track: Track | undefined, lane: number): boolean {
  return Boolean(track?.takeNames[String(lane)]?.trim());
}

/**
 * Renames a lane, or clears the name when given an empty one.
 *
 * Clearing rather than storing "" matters: an empty stored name would suppress
 * the fallback and leave the lane with no label at all.
 *
 * A name that happens to read like the default is still stored. It looks
 * redundant and is not: recording pins the pass number as a real name, and that
 * pin is exactly what stops a later delete from renumbering the performance.
 */
export function setTakeName(names: Track['takeNames'], lane: number, name: string): Track['takeNames'] {
  const trimmed = name.trim().slice(0, MAX_TAKE_NAME);
  const next = { ...names };
  if (!trimmed) delete next[String(lane)];
  else next[String(lane)] = trimmed;
  return next;
}

/**
 * Moves the names down to follow their lanes after one lane is deleted.
 *
 * The deleted lane's name goes with it; every name above slides down by one,
 * because that is exactly what its clips do.
 */
export function takeNamesAfterRemoval(names: Track['takeNames'], removed: number): Track['takeNames'] {
  const next: Track['takeNames'] = {};
  for (const [key, value] of Object.entries(names)) {
    const lane = Number(key);
    if (!Number.isFinite(lane) || lane === removed) continue;
    next[String(lane > removed ? lane - 1 : lane)] = value;
  }
  return next;
}

/**
 * A name for a pass that has not been recorded yet.
 *
 * It counts performances, not lanes: record three, delete the second, record
 * again, and the new one is "Take 4" even though it lands on lane 3. Reusing
 * "Take 3" would file two different performances under one name, which is the
 * confusion this module exists to avoid. Numbering only ever goes up.
 */
export function nextTakeName(project: Project, trackId: string): string {
  const track = project.tracks[trackId];
  if (!track) return defaultTakeName(1);

  let highest = 0;
  for (const clipId of track.clipIds) {
    const lane = project.clips[clipId]?.takeLane ?? 0;
    if (lane > 0) highest = Math.max(highest, lane);
  }
  // A stored name of the form "Take N" counts as having used N, wherever its
  // lane ended up. That is what keeps the numbering ascending across a delete:
  // the pass called "Take 3" still exists, so the next one is 4 even though it
  // lands on lane 3.
  for (const value of Object.values(track.takeNames)) {
    const match = /^Take (\d+)$/.exec(value.trim());
    if (match) highest = Math.max(highest, Number(match[1]));
  }
  return defaultTakeName(highest + 1);
}

/**
 * Where the comp came from.
 *
 * A comped clip is a slice of a take, copied onto lane 0 — an ordinary clip,
 * with no note of which take it was cut from. That is what keeps takes small
 * (see docs/TAKES_AND_COMPING.md), and it is fine right up to the moment the
 * user asks "which take is this?", or looks at four lanes of takes and wants
 * to see which parts of them are being heard.
 *
 * The answer is derived rather than stored: a comp clip came from a take if
 * promoting that take over the comp clip's span would produce exactly this
 * clip — same audio at the same source position, or the same notes. That is
 * the definition of a comp edit, so it cannot drift from what promotion does,
 * and a clip the user dragged up by hand, or trimmed afterwards, is still
 * recognised as long as it still is what the take holds there. Two takes
 * holding identical material tie, and the lower lane wins; nothing is lost by
 * that, because they are identical.
 */

export interface CompSource {
  clipId: string;
  /** The take lane the clip's material is from, or null when no take holds it. */
  takeLane: number | null;
}

/** Whether two clips are the same material over the same span: what a comp is. */
function sameMaterial(a: Clip, b: Clip): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'audio' && b.kind === 'audio') {
    return (
      a.assetId === b.assetId &&
      Math.abs(a.offsetSec - b.offsetSec) < 1e-3 &&
      Math.abs(a.durationSec - b.durationSec) < 1e-3 &&
      a.reversed === b.reversed &&
      a.playbackRate === b.playbackRate &&
      a.warpSourceBpm === b.warpSourceBpm
    );
  }
  if (a.kind === 'midi' && b.kind === 'midi') {
    const key = (clip: MidiClip): string =>
      clip.notes
        .map((n) => `${n.pitch}:${n.startTicks}:${n.durationTicks}`)
        .sort()
        .join('|');
    return a.notes.length === b.notes.length && key(a) === key(b);
  }
  return false;
}

/** The take each comp-lane clip on a track was cut from. */
export function compSources(project: Project, trackId: string): CompSource[] {
  const track = project.tracks[trackId];
  if (!track) return [];
  const comp = clipsOnLane(project, trackId, 0);
  const lanes = maxTakeLane(project, trackId);
  return comp.map((clip) => {
    const end = clipEndTicks(project, clip);
    for (let lane = 1; lane <= lanes; lane++) {
      for (const take of clipsOnLane(project, trackId, lane)) {
        // Compared where the two overlap: a comp clip's crossfade can reach a
        // little past the take clip's own trim, into the same recording, and
        // that does not make it somebody else's take.
        const from = Math.max(take.startTicks, clip.startTicks);
        const to = Math.min(clipEndTicks(project, take), end);
        if (to <= from) continue;
        const ofTake = sliceClip(project, take, from, to, take.id);
        const ofComp = sliceClip(project, clip, from, to, clip.id);
        if (ofTake && ofComp && sameMaterial(ofTake, ofComp)) return { clipId: clip.id, takeLane: lane };
      }
    }
    return { clipId: clip.id, takeLane: null };
  });
}

/** The spans of a take lane, in ticks, that the comp is currently made of. */
export function takeSpansInComp(project: Project, trackId: string, takeLane: number): { startTicks: number; endTicks: number }[] {
  return compSources(project, trackId)
    .filter((source) => source.takeLane === takeLane)
    .map((source) => {
      const clip = project.clips[source.clipId];
      return { startTicks: clip.startTicks, endTicks: clipEndTicks(project, clip) };
    })
    .sort((a, b) => a.startTicks - b.startTicks);
}
